/**
 * ─── Arena Fighters Page ────────────────────────────────────
 * CRUD for fighters with breed, weight class, record tracking.
 */

import { useEffect, useState, useCallback } from 'react'
import { Card, CardHeader, Badge, Button, Input, Modal } from '@repo/ui-core'
import { arenaFighterRepo } from '../../lib/db'
import { FIGHTER_STATUS_LABELS, WEIGHT_CLASS_LABELS } from '@repo/entity-arena'
import { Swords, Plus, Pencil, Trash2, Trophy, TrendingDown } from 'lucide-react'

export function ArenaFighterPage() {
  const [fighters, setFighters] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ name: '', breed: '', ownerName: '', ownerContact: '',
    weight: 0, weightClass: 'open', age: 0, color: '', status: 'active', notes: '' })

  const load = useCallback(async () => {
    setLoading(true)
    try { const r = await arenaFighterRepo.findMany({ page: 1, pageSize: 500 }); setFighters('items' in r ? r.items : []) }
    catch (e) { console.error(e) } finally { setLoading(false) }
  }, [])
  useEffect(() => { load() }, [load])

  async function save() {
    setSaving(true)
    try {
      const payload: any = { ...form, weight: Number(form.weight), age: Number(form.age),
        wins: 0, losses: 0, draws: 0, fighterCode: 'FTR-' + Date.now().toString(36).toUpperCase().slice(-6),
        tenantId: 'default', createdBy: 'admin', updatedBy: 'admin' }
      if (editId) await arenaFighterRepo.update(editId, { ...payload, updatedBy: 'admin' })
      else await arenaFighterRepo.create(payload)
      setShowModal(false); reset(); await load()
    } catch (e) { console.error(e) } finally { setSaving(false) }
  }

  function reset() { setForm({ name: '', breed: '', ownerName: '', ownerContact: '', weight: 0, weightClass: 'open', age: 0, color: '', status: 'active', notes: '' }); setEditId(null) }
  function edit(f: any) { setForm({ name: f.name, breed: f.breed, ownerName: f.ownerName, ownerContact: f.ownerContact ?? '', weight: f.weight, weightClass: f.weightClass, age: f.age, color: f.color ?? '', status: f.status, notes: f.notes ?? '' }); setEditId(f.id); setShowModal(true) }
  async function del(id: string) { if (!confirm('Delete?')) return; try { await arenaFighterRepo.delete(id); await load() } catch (e) { console.error(e) } }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div><h2 className="text-2xl font-bold text-gray-900">Fighters</h2><p className="text-sm text-gray-500 mt-1">{fighters.length} registered</p></div>
        <Button onClick={() => { reset(); setShowModal(true) }} icon={<Plus className="h-4 w-4" />}>Add Fighter</Button>
      </div>
      <Card><CardHeader title="Fighter Roster" description="Breed, weight class, record" />
        <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50"><tr>{['Name','Breed','Owner','Weight','Class','W','L','D','Status',''].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {loading ? <tr><td colSpan={10} className="py-8 text-center text-gray-400">Loading...</td></tr> :
             fighters.length === 0 ? <tr><td colSpan={10} className="py-8 text-center text-gray-400">No fighters yet.</td></tr> :
             fighters.map((f: any) => (
              <tr key={f.id} className="hover:bg-gray-50">
                <td className="px-3 py-2.5 font-medium text-gray-900">{f.name}</td>
                <td className="px-3 py-2.5 text-gray-500">{f.breed}</td>
                <td className="px-3 py-2.5 text-gray-500 text-xs">{f.ownerName}</td>
                <td className="px-3 py-2.5 text-gray-600">{f.weight}g</td>
                <td className="px-3 py-2.5"><Badge color="blue">{WEIGHT_CLASS_LABELS[f.weightClass as keyof typeof WEIGHT_CLASS_LABELS] ?? f.weightClass}</Badge></td>
                <td className="px-3 py-2.5 text-green-600 font-medium">{f.wins ?? 0}</td>
                <td className="px-3 py-2.5 text-red-600">{f.losses ?? 0}</td>
                <td className="px-3 py-2.5 text-gray-500">{f.draws ?? 0}</td>
                <td className="px-3 py-2.5"><Badge color={f.status==='active'?'green':f.status==='injured'?'yellow':'red'}>{FIGHTER_STATUS_LABELS[f.status as keyof typeof FIGHTER_STATUS_LABELS]??f.status}</Badge></td>
                <td className="px-3 py-2.5"><div className="flex gap-1">
                  <button onClick={()=>edit(f)} className="rounded p-1 hover:bg-gray-100"><Pencil className="h-3.5 w-3.5 text-blue-600"/></button>
                  <button onClick={()=>del(f.id)} className="rounded p-1 hover:bg-gray-100"><Trash2 className="h-3.5 w-3.5 text-red-600"/></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Card>
      <Modal open={showModal} onClose={()=>{setShowModal(false);reset()}} title={editId?'Edit Fighter':'Add Fighter'} size="md"
        footer={<div className="flex gap-2"><Button variant="secondary" onClick={()=>{setShowModal(false);reset()}}>Cancel</Button><Button onClick={save} loading={saving} icon={<Swords className="h-4 w-4"/>}>{editId?'Update':'Create'}</Button></div>}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label="Name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} required />
          <Input label="Breed" value={form.breed} onChange={e=>setForm({...form,breed:e.target.value})} />
          <Input label="Owner Name" value={form.ownerName} onChange={e=>setForm({...form,ownerName:e.target.value})} />
          <Input label="Owner Contact" value={form.ownerContact} onChange={e=>setForm({...form,ownerContact:e.target.value})} />
          <Input label="Weight (g)" type="number" value={String(form.weight)} onChange={e=>setForm({...form,weight:Number(e.target.value)})} />
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Weight Class</label>
            <select value={form.weightClass} onChange={e=>setForm({...form,weightClass:e.target.value})} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              {Object.entries(WEIGHT_CLASS_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}</select></div>
          <Input label="Age (months)" type="number" value={String(form.age)} onChange={e=>setForm({...form,age:Number(e.target.value)})} />
          <Input label="Color" value={form.color} onChange={e=>setForm({...form,color:e.target.value})} />
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select value={form.status} onChange={e=>setForm({...form,status:e.target.value})} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              {Object.entries(FIGHTER_STATUS_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}</select></div>
          <div className="sm:col-span-2"><Input label="Notes" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} /></div>
        </div>
      </Modal>
    </div>
  )
}
